/**
 * In-chat calendar embed component.
 *
 * Renders a Cal.com or Calendly calendar as a message-like card inside the
 * chat conversation area. Listens for PostMessage booking confirmations and
 * auto-closes after a successful booking.
 */

import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CHAT_CONFIG } from '../config/constants';
import { useChatLanguage } from '../context/ChatThemeContext';
import { logger } from '../utils/core/logger';
import { isMobileDevice } from '../utils/device/mobileDetection';
import {
    BOOKING_PROVIDER_HOSTS,
    buildBookingEmbedUrl,
    isBookingSuccessMessage,
} from '../utils/web/bookingProviders';

interface InChatCalendarEmbedProps {
    url: string;
    onBack: () => void;
    onBookingCompleted?: () => void;
    primaryColor: string;
}

function isAllowedOrigin(origin: string): boolean {
    try {
        const hostname = new URL(origin).hostname;
        return BOOKING_PROVIDER_HOSTS.some((d) => hostname === d || hostname.endsWith(`.${d}`));
    } catch {
        return false;
    }
}

/** Timeout in ms before showing a load-failure fallback. */
const IFRAME_LOAD_TIMEOUT_MS = 15_000;

/** Minimum time (ms) to show the spinner, letting the calendar render behind it. */
const MIN_SPINNER_MS = 2_000;

/** SSR / no-window fallback height for the calendar card. */
const DESKTOP_CALENDAR_HEIGHT = 400;

/** Visual breathing room above the embedded calendar card. */
const IN_CHAT_CALENDAR_TOP_PADDING_PX = 20;

const CALENDAR_COPY = {
    en: {
        confirmed: 'Booking confirmed!',
        failed: 'Calendar failed to load',
        openInNewTab: 'Open calendar in new tab',
    },
    fr: {
        confirmed: 'Réservation confirmée !',
        failed: 'Le calendrier n’a pas pu être chargé',
        openInNewTab: 'Ouvrir le calendrier dans un nouvel onglet',
    },
    de: {
        confirmed: 'Termin bestätigt!',
        failed: 'Kalender konnte nicht geladen werden',
        openInNewTab: 'Kalender in neuem Tab öffnen',
    },
    es: {
        confirmed: '¡Reserva confirmada!',
        failed: 'No se pudo cargar el calendario',
        openInNewTab: 'Abrir el calendario en una pestaña nueva',
    },
    it: {
        confirmed: 'Prenotazione confermata!',
        failed: 'Impossibile caricare il calendario',
        openInNewTab: 'Apri il calendario in una nuova scheda',
    },
    pt: {
        confirmed: 'Reserva confirmada!',
        failed: 'Não foi possível carregar o calendário',
        openInNewTab: 'Abrir o calendário num novo separador',
    },
    bg: {
        confirmed: 'Резервацията е потвърдена!',
        failed: 'Календарът не можа да се зареди',
        openInNewTab: 'Отворете календара в нов раздел',
    },
    nl: {
        confirmed: 'Boeking bevestigd!',
        failed: 'De kalender kon niet worden geladen',
        openInNewTab: 'Open kalender in een nieuw tabblad',
    },
    ro: {
        confirmed: 'Programare confirmată!',
        failed: 'Calendarul nu a putut fi încărcat',
        openInNewTab: 'Deschideți calendarul într-o filă nouă',
    },
    hu: {
        confirmed: 'A foglalás megerősítve!',
        failed: 'A naptár nem tölthető be',
        openInNewTab: 'Naptár megnyitása új lapon',
    },
} as const;

/** Vertical space reserved for header + search bar on mobile. */
const MOBILE_CHROME_PX = 140;

/** Vertical space taken by the header + input overlay + top padding on desktop. */
const DESKTOP_CHROME_PX = 152;

/**
 * Compute the calendar embed height in pixels so the card fills the widget
 * panel (the calendar matches the widget's height rather than the widget
 * growing to the calendar's natural ~1200px content).
 * - Mobile: fill (nearly) the full viewport so Cal.com's mobile picker is usable.
 * - Desktop: fill the expanded panel, whose height mirrors ExpandedChatView's
 *   `min(max(67vh, 536px), 600px)`, minus the header/input chrome.
 */
function getInChatCalendarHeight(): number {
    if (typeof window === 'undefined') return DESKTOP_CALENDAR_HEIGHT;
    if (isMobileDevice()) {
        return Math.max(DESKTOP_CALENDAR_HEIGHT, window.innerHeight - MOBILE_CHROME_PX);
    }
    const panelHeight = Math.min(Math.max(window.innerHeight * 0.67, 536), 600);
    return Math.round(panelHeight - DESKTOP_CHROME_PX);
}

/** Total rendered height of the in-chat calendar block, including the top offset wrapper. */
export function getInChatCalendarRenderedHeight(): number {
    return getInChatCalendarHeight() + IN_CHAT_CALENDAR_TOP_PADDING_PX;
}

const InChatCalendarEmbed: React.FC<InChatCalendarEmbedProps> = ({
    url,
    onBack,
    onBookingCompleted,
    primaryColor,
}) => {
    const language = useChatLanguage();
    const copy = CALENDAR_COPY[language];
    const embedUrl = buildBookingEmbedUrl(url);
    const [isLoading, setIsLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [bookingConfirmed, setBookingConfirmed] = useState(false);
    const bookingFiredRef = useRef(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [calendarHeight, setCalendarHeight] = useState(() => getInChatCalendarHeight());

    // Re-compute height on viewport resize (e.g. device rotation)
    useEffect(() => {
        const onResize = () => setCalendarHeight(getInChatCalendarHeight());
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const clearLoadTimeout = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    // The iframe loads in the background while the spinner is visible.
    // We track two conditions: the minimum spinner time has elapsed AND the
    // calendar is ready (iframe loaded or PostMessage received).
    const minTimeElapsedRef = useRef(false);
    const calendarReadyRef = useRef(false);

    const revealIfReady = useCallback(() => {
        if (minTimeElapsedRef.current && calendarReadyRef.current) {
            clearLoadTimeout();
            setIsLoading(false);
        }
    }, [clearLoadTimeout]);

    const handleIframeLoad = useCallback(() => {
        // Treat iframe load as the fallback readiness signal for all providers.
        // Cal.com can still reveal via PostMessage earlier/later, but a loaded iframe
        // should never end up in the timeout failure state just because that message
        // did not arrive.
        calendarReadyRef.current = true;
        revealIfReady();
    }, [revealIfReady]);

    // Minimum spinner duration — let the calendar render behind our spinner
    useEffect(() => {
        const timer = setTimeout(() => {
            logger.debug('📅 [InChatEmbed] Minimum spinner time elapsed');
            minTimeElapsedRef.current = true;
            revealIfReady();
        }, MIN_SPINNER_MS);
        return () => clearTimeout(timer);
    }, [revealIfReady]);

    // Hard timeout — show the calendar (or failure) after 15s no matter what
    useEffect(() => {
        timeoutRef.current = setTimeout(() => {
            logger.debug('📅 [InChatEmbed] Iframe load timed out after 15s');
            setIsLoading(false);
            if (!calendarReadyRef.current) {
                setLoadFailed(true);
            }
        }, IFRAME_LOAD_TIMEOUT_MS);

        return clearLoadTimeout;
        // eslint-disable-next-line react-hooks/exhaustive-deps -- single-shot timeout on mount
    }, [clearLoadTimeout]);

    // Listen for PostMessages from calendar providers.
    // Cal.com sends messages (e.g. __dimensionChanged) once its React app
    // renders — we use the first one to mark it ready.
    useEffect(() => {
        const handler = (event: MessageEvent) => {
            if (!isAllowedOrigin(event.origin)) return;

            let data = event.data;
            if (typeof data === 'string') {
                try {
                    data = JSON.parse(data);
                } catch {
                    return;
                }
            }
            if (!data || typeof data !== 'object') return;

            // Any PostMessage from Cal.com means the app has rendered
            if (!calendarReadyRef.current && data?.originator === 'CAL') {
                logger.debug('📅 [InChatEmbed] Cal.com app ready (first PostMessage)');
                calendarReadyRef.current = true;
                revealIfReady();
            }

            if (isBookingSuccessMessage(data)) {
                // Cal.com / Calendly can emit multiple booking PostMessages for a single
                // booking. Dedupe so onBookingCompleted (which triggers a streaming request
                // + redirect) fires exactly once and we don't hit the per-session
                // concurrency guard with a duplicate marker.
                if (bookingFiredRef.current) return;
                bookingFiredRef.current = true;
                logger.debug('📅 [InChatEmbed] Booking confirmed via PostMessage');
                setBookingConfirmed(true);
                setTimeout(() => {
                    onBookingCompleted?.();
                    onBack();
                }, 2000);
            }
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [onBack, onBookingCompleted, revealIfReady]);

    if (bookingConfirmed) {
        return (
            <div className="w-full" style={{ paddingTop: `${IN_CHAT_CALENDAR_TOP_PADDING_PX}px` }}>
                <div
                    className="rounded-2xl border p-6 text-center"
                    style={{
                        backgroundColor: CHAT_CONFIG.COLORS.BACKGROUND.WHITE,
                        borderColor: CHAT_CONFIG.COLORS.BORDERS.LIGHT,
                        height: `${calendarHeight}px`,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <div className="text-4xl mb-3">&#10003;</div>
                    <p className="text-base font-medium" style={{ color: primaryColor }}>
                        {copy.confirmed}
                    </p>
                </div>
            </div>
        );
    }

    if (loadFailed) {
        return (
            <div className="w-full" style={{ paddingTop: `${IN_CHAT_CALENDAR_TOP_PADDING_PX}px` }}>
                <div
                    className="rounded-2xl border p-6 text-center"
                    style={{
                        backgroundColor: CHAT_CONFIG.COLORS.BACKGROUND.WHITE,
                        borderColor: CHAT_CONFIG.COLORS.BORDERS.LIGHT,
                        height: `${calendarHeight}px`,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <p
                        className="text-base font-medium mb-4"
                        style={{ color: CHAT_CONFIG.COLORS.TEXT.PRIMARY }}
                    >
                        {copy.failed}
                    </p>
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white no-underline"
                        style={{ backgroundColor: primaryColor }}
                    >
                        {copy.openInNewTab}
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full" style={{ paddingTop: `${IN_CHAT_CALENDAR_TOP_PADDING_PX}px` }}>
            <div
                className="rounded-2xl border overflow-hidden relative"
                style={{
                    backgroundColor: CHAT_CONFIG.COLORS.BACKGROUND.WHITE,
                    borderColor: CHAT_CONFIG.COLORS.BORDERS.LIGHT,
                    height: `${calendarHeight}px`,
                }}
            >
                {isLoading && (
                    <div
                        className="absolute inset-0 flex items-center justify-center z-10"
                        style={{ backgroundColor: CHAT_CONFIG.COLORS.BACKGROUND.WHITE }}
                    >
                        <div
                            className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
                            style={{
                                borderColor: `${primaryColor} transparent ${primaryColor} ${primaryColor}`,
                            }}
                        />
                    </div>
                )}
                <iframe
                    src={embedUrl}
                    title="Book a meeting"
                    className="w-full h-full border-0"
                    allow="payment"
                    onLoad={handleIframeLoad}
                />
            </div>
        </div>
    );
};

export default InChatCalendarEmbed;
